import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ScaleControl } from "react-map-gl/mapbox";
import {
  type SharedAdventureMap,
  getMapByShareId,
  getGeoJsonPlacesByShareId,
  getPlaceTrack,
  type PlaceFeature,
  type PlacePreviewFeature,
  type RelatedPlace,
  relatedPlaceToPreview,
} from "../lib/api";
import { emptyPlacesGeoJSON, FIT_BOUNDS_PADDING, FLY_TO_ZOOM } from "../lib/mapConstants";
import { routes } from "../lib/routes";
import { useMapSelection } from "../lib/mapSelectionStore";
import { useInstrumentation } from "../hooks/useInstrumentation";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { usePlaceNavigation } from "../hooks/usePlaceNavigation";
import { useShare } from "../hooks/useShare";
import { Map, useViewport, usePanel, usePanelPosition } from "../components/map";
import { toDetailLevel } from "~/lib/detailLevel";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { PageHead } from "../components/PageHead";
import { ViewerPanelContent } from "../components/explorer";
import {
  AdventureMapPlacesLayer,
  CompassControl,
  RelatedPlacesLayer,
  RouteTrackLayer,
  SelectedPlaceLayer,
  ZoomControls,
} from "../components/map";

const viewerEmptyState = (
  <div className="px-4 py-8 text-center text-muted-foreground">
    <p className="text-sm">No places on this map yet</p>
  </div>
);

export default function MapViewer() {
  const { identifier } = useParams<{ identifier: string }>();
  const navigate = useNavigate();
  const { below } = useBreakpoint();
  const isMobile = below("md");
  const instrumentation = useInstrumentation();
  const share = useShare();
  const { flyTo, fitBounds, isReady } = useViewport();
  const { position, snapTo } = usePanel();
  const { selected: selectedPlace, select: selectPlace, deselect: deselectPlace } = useMapSelection();

  usePanelPosition("preview", !!selectedPlace);

  const [mapData, setMapData] = useState<SharedAdventureMap | null>(null);
  const [places, setPlaces] = useState(emptyPlacesGeoJSON);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const details = toDetailLevel(position);

  // Navigation callback: handles place ID (from URL) or preview feature (from history state)
  const handlePlaceChange = useCallback(
    (data: string | PlacePreviewFeature | null) => {
      if (!data) {
        deselectPlace();
        return;
      }

      // Preview feature from history state - use directly
      if (typeof data !== "string") {
        selectPlace(data);
        return;
      }

      // Place ID from URL - look up the feature
      const place = places.features.find((f) => f.id === data);
      if (place) {
        selectPlace(place);
      }
    },
    [places.features, selectPlace, deselectPlace],
  );

  const { navigateToPlace, clearPlaceSelection, originPlaceId } = usePlaceNavigation({
    selectedPlaceId: selectedPlace?.properties.id ?? null,
    onPlaceChange: handlePlaceChange,
  });

  // Re-sync URL → state when places load (initial load may happen before places are fetched)
  const [searchParams] = useSearchParams();
  const urlPlaceId = searchParams.get("place");
  useEffect(() => {
    if (!urlPlaceId || selectedPlace) return;
    handlePlaceChange(urlPlaceId);
  }, [urlPlaceId, selectedPlace, places.features, handlePlaceChange]);

  // Fly to selected place when it changes (includes related places in viewport if any)
  useEffect(() => {
    if (!selectedPlace || !isReady) return;
    const center = selectedPlace.geometry.coordinates as [number, number];
    const fitWith =
      selectedPlace.properties.relatedPlaces?.map((p) => [p.longitude, p.latitude] as [number, number]) ?? [];
    flyTo(center, { zoom: FLY_TO_ZOOM, fitWith });
  }, [selectedPlace, isReady, flyTo]);

  // Load map and places data
  useEffect(() => {
    if (!identifier) return;

    setLoading(true);
    Promise.all([getMapByShareId(identifier), getGeoJsonPlacesByShareId(identifier)])
      .then(([mapResult, placesData]) => {
        // Redirect to landing page if published
        if (mapResult.landingPage?.publishedAt && mapResult.landingPage.slug) {
          window.location.replace(routes.to.landingPages.bySlug(mapResult.landingPage.slug));
          return;
        }
        setMapData(mapResult);
        setPlaces(placesData);
        instrumentation.track("Adventure Map Viewed", {
          mapId: mapResult.shareId,
          placeCount: placesData.features.length,
        });
      })
      .catch((err) => {
        console.error(err);
        setError("Map not found");
      })
      .finally(() => setLoading(false));

    // Reset selection when changing maps
    return () => {
      deselectPlace();
    };
  }, [identifier, instrumentation, deselectPlace]);

  // Fit bounds when places load
  const hasFittedBounds = useRef(false);
  useEffect(() => {
    if (!hasFittedBounds.current && isReady && places.features.length > 0) {
      hasFittedBounds.current = true;
      fitBounds(places, isMobile ? FIT_BOUNDS_PADDING.mobile : FIT_BOUNDS_PADDING.desktop);
    }
  }, [places, isReady, fitBounds, isMobile]);

  const handlePlaceClick = useCallback(
    (feature: PlaceFeature) => {
      if (mapData) {
        instrumentation.track("Place Selected", {
          map: mapData.shareId,
          placeId: feature.id as string,
          placeType: feature.properties.type,
          context: "adventure_map",
        });
      }
      navigateToPlace(feature);
    },
    [navigateToPlace, mapData, instrumentation],
  );

  const handleShare = useCallback(() => {
    if (mapData?.shareId) {
      share({ url: routes.to.maps.publicViewUrl(mapData.name, mapData.shareId), title: mapData.name });
    }
  }, [share, mapData]);

  const handleBack = useCallback(() => {
    if (originPlaceId) {
      navigate(-1);
    } else {
      clearPlaceSelection();
    }
  }, [originPlaceId, navigate, clearPlaceSelection]);

  const handleSelectRelatedPlace = useCallback(
    (relatedPlace: RelatedPlace) => {
      snapTo("preview");
      const feature = places.features.find((f) => f.id === relatedPlace.id) ?? relatedPlaceToPreview(relatedPlace);
      navigateToPlace(feature, { originPlaceId: selectedPlace?.properties.id, preview: true });
    },
    [snapTo, places.features, navigateToPlace, selectedPlace],
  );

  if (loading) {
    return (
      <div className="absolute inset-0 flex items-center justify-center p-4 pointer-events-none">
        <Card className="p-8 text-center text-muted-foreground pointer-events-auto">Loading map...</Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="absolute inset-0 flex items-center justify-center p-4 pointer-events-none">
        <Card className="p-8 text-center pointer-events-auto">
          <p className="text-destructive mb-4">{error}</p>
          <Button asChild variant="secondary">
            <Link to={routes.to.home()}>Go home</Link>
          </Button>
        </Card>
      </div>
    );
  }

  // SEO - noindex for viewer, OG/Twitter for social sharing
  const pageDescription = mapData?.description || `Explore ${mapData?.name} - curated outdoor adventures`;
  const firstImagePlace = places.features.find((f) => f.properties.images.length > 0);
  const ogImage = firstImagePlace?.properties.images[0]?.large;

  return (
    <>
      {mapData && (
        <PageHead
          title={mapData.name}
          description={pageDescription}
          image={ogImage}
          author={mapData.curatedBy?.displayName}
          noIndex
        />
      )}

      {/* Scale control (stays in mapbox layer) */}
      <ScaleControl position="bottom-left" maxWidth={100} unit="metric" />

      {/* Map controls */}
      <Map.Controls>
        <CompassControl />
        <ZoomControls />
      </Map.Controls>

      {/* Route track for selected place (render first = bottom layer) */}
      <RouteTrackLayer track={selectedPlace ? getPlaceTrack(selectedPlace.properties) : undefined} />

      {/* Adventure map places layer */}
      <AdventureMapPlacesLayer
        places={places}
        excludeIds={selectedPlace?.properties.relatedPlaces?.map((p) => p.id)}
        onClick={handlePlaceClick}
      />

      {/* Related places layer (smaller pins showing context around selected place) */}
      <RelatedPlacesLayer
        relatedPlaces={selectedPlace?.properties.relatedPlaces ?? []}
        onClick={handleSelectRelatedPlace}
      />

      {/* Selected place layer */}
      <SelectedPlaceLayer place={selectedPlace} />

      {/* Side panel (desktop) / Bottom sheet (mobile) */}
      {mapData && (
        <Map.Panel>
          <ViewerPanelContent
            map={mapData}
            places={places}
            selectedPlace={selectedPlace}
            details={details}
            onPlaceDeselect={handleBack}
            onSelectRelatedPlace={handleSelectRelatedPlace}
            onShare={handleShare}
            emptyState={viewerEmptyState}
          />
        </Map.Panel>
      )}
    </>
  );
}
